import {
    Anchor,
    Box,
    Button,
    Card,
    Collapse,
    Container,
    Flex,
    Image,
    PasswordInput,
    Stack,
    Text,
    TextInput,
    Title,
    LoadingOverlay,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useAuthMutation } from "../services/api";
import { useSelector } from "../hooks";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useAnalytics from "../hooks/useAnalytics";
import { refreshCsrfToken } from "../utils/csrfToken";
import { format, parse } from "date-fns";
import csLogo from "../assets/cs-horizontal-logo@3x.png";
import { useSidebar } from "../context/layoutContext";
import { LuChevronDown, LuChevronUp } from "react-icons/lu";

function sanitizeSsn(value: string): string {
    return value.replace(/\D/g, "").slice(0, 9);
}

export default function Login() {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birth, setBirth] = useState<Date>();
    const [subscriberNumber, setSubscriberNumber] = useState("");
    const [showSsnBackup, setShowSsnBackup] = useState(false);
    const [ssn, setSsn] = useState("");

    // Track which fields have been tracked to avoid duplicate events
    const [trackedFields, setTrackedFields] = useState<Set<string>>(new Set());

    const [auth, result] = useAuthMutation();
    const isAuthed = useSelector((state) => state.account.isAuthed);
    const { checkAuth } = useAuth();
    const { trackEvent, AnalyticsEvents } = useAnalytics();

    const navigate = useNavigate();

    useSidebar(false);

    // Clear the SSN value any time it's no longer relevant: on successful
    // login, on unmount, or if the member re-collapses the SSN section.
    // SSN must never persist beyond the active submit action.
    useEffect(() => {
        if (!showSsnBackup) {
            setSsn("");
        }
    }, [showSsnBackup]);

    useEffect(() => {
        return () => {
            setSsn("");
        };
    }, []);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        switch (event.target.name) {
            case "firstName": {
                setFirstName(value);
                break;
            }
            case "lastName": {
                setLastName(value);
                break;
            }
            case "birth": {
                setBirth(parse(value, "yyyy-MM-dd", new Date()));
                break;
            }
            case "subscriberNumber": {
                setSubscriberNumber(value);
                break;
            }
            case "ssn": {
                setSsn(sanitizeSsn(value));
                break;
            }
        }
    };

    const handleSsnToggle = () => {
        const next = !showSsnBackup;
        setShowSsnBackup(next);
        if (next) {
            trackEvent(AnalyticsEvents.SSN_BACKUP_EXPANDED);
        }
    };

    // Track field entry on blur (only if field has value and hasn't been tracked yet)
    const handleFieldBlur = (fieldName: string) => {
        // Skip if already tracked
        if (trackedFields.has(fieldName)) {
            return;
        }
        
        // Check if field has a value before tracking
        let hasValue = false;
        switch (fieldName) {
            case "firstName":
                hasValue = firstName.trim().length > 0;
                if (hasValue) {
                    trackEvent(AnalyticsEvents.FIELD_ENTRY_FIRST_NAME);
                }
                break;
            case "lastName":
                hasValue = lastName.trim().length > 0;
                if (hasValue) {
                    trackEvent(AnalyticsEvents.FIELD_ENTRY_LAST_NAME);
                }
                break;
            case "birth":
                hasValue = birth !== undefined;
                if (hasValue) {
                    trackEvent(AnalyticsEvents.FIELD_ENTRY_DOB);
                }
                break;
            case "subscriberNumber":
                hasValue = subscriberNumber.trim().length > 0;
                if (hasValue) {
                    trackEvent(AnalyticsEvents.FIELD_ENTRY_MEMBER_ID);
                }
                break;
            case "ssn":
                hasValue = ssn.trim().length > 0;
                if (hasValue) {
                    trackEvent(AnalyticsEvents.FIELD_ENTRY_SSN);
                }
                break;
        }
        
        // Mark field as tracked if it had a value
        if (hasValue) {
            setTrackedFields(new Set(trackedFields).add(fieldName));
        }
    };

    useEffect(() => {
        if (isAuthed) {
            navigate("/", { replace: true });
        }
    }, [isAuthed, navigate]);

    useEffect(() => {
        if (result.isSuccess) {
            trackEvent(AnalyticsEvents.LOGIN_SUCCESS);
            setSsn("");
            checkAuth();
            // The CSRF session identifier switches from the anonymous
            // pre-login cookie to the authenticated JWT cookie once logged
            // in, so any CSRF token fetched before this point is now stale.
            // A failure here shouldn't block navigation; the next mutating
            // call will simply retry the fetch (see csrfToken.ts).
            refreshCsrfToken().catch(() => undefined);
            navigate("/");
        }
    }, [checkAuth, navigate, result, trackEvent, AnalyticsEvents]);

    // Track login error when it occurs
    useEffect(() => {
        if (result.isError) {
            trackEvent(AnalyticsEvents.LOGIN_ERROR_DISPLAYED, {
                error_message: "There was an error logging you in. Please check the information and try again.",
            });
            setSsn("");
        }
    }, [result.isError, trackEvent, AnalyticsEvents]);

    const usingSsnBackup = showSsnBackup && !subscriberNumber && ssn.length === 9;

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        // Track login button click
        trackEvent(AnalyticsEvents.LOGIN_CLICKED);

        auth({
            firstName: firstName,
            lastName: lastName,
            birth: birth ? format(birth, "MM/dd/yyyy") : birth,
            id: subscriberNumber || undefined,
            ssn: usingSsnBackup ? ssn : undefined,
        });
    };

    return (
        <Container size={"xs"}>
            <Card
                shadow="md"
                radius="md"
                withBorder
                my={"sm"}
                mx={"auto"}
                p={"lg"}
            >
                <LoadingOverlay visible={result?.isLoading} overlayBlur={2} />
                <Box
                    display="flex"
                    sx={{
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                    py="md"
                >
                    <Image src={csLogo} alt="CareSource Logo" />
                    <Title order={3} size="h3" pt="md">
                        Member Info Viewer
                    </Title>
                </Box>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={"lg"} mt={"md"}>
                        <TextInput
                            label="First Name"
                            required
                            name="firstName"
                            onChange={handleChange}
                            onBlur={() => handleFieldBlur("firstName")}
                            description="First Name exactly as it appears on your member ID card."
                        />
                        <TextInput
                            label="Last Name"
                            required
                            name="lastName"
                            onChange={handleChange}
                            onBlur={() => handleFieldBlur("lastName")}
                            description="Last Name exactly as it appears on your member ID
                        card."
                        />
                        <TextInput
                            label="Birth Date"
                            description="Please enter your birth date."
                            required
                            type="date"
                            pattern="\d{4}-\d{2}-\d{2}" // Needed as fallback. Most modern browsers support this, but maybe add detection to show proper format if needed?
                            name="birth"
                            max={format(new Date(), "yyyy-MM-dd")}
                            onChange={handleChange}
                            onBlur={() => handleFieldBlur("birth")}
                        />
                        <TextInput
                            label="Member ID Number"
                            description="You can find this 11 or 12-digit number on your ID
                        card."
                            required={!usingSsnBackup}
                            minLength={11}
                            maxLength={12}
                            name="subscriberNumber"
                            onChange={handleChange}
                            onBlur={() => handleFieldBlur("subscriberNumber")}
                        />

                        <Box>
                            <Anchor
                                component="button"
                                type="button"
                                size="sm"
                                color="cs-purple.5"
                                onClick={handleSsnToggle}
                                data-testid="ssn-backup-toggle"
                            >
                                <Flex align={"center"} gap={4}>
                                    <Text size="sm" fw={700} color="inherit">
                                        I don&apos;t have my ID number
                                    </Text>
                                    {showSsnBackup ? (
                                        <LuChevronUp />
                                    ) : (
                                        <LuChevronDown />
                                    )}
                                </Flex>
                            </Anchor>
                            <Collapse in={showSsnBackup}>
                                <Text fz="sm" mb={"md"}>
                                    No problem. You can use your Social Security
                                    Number instead.
                                </Text>
                                <PasswordInput
                                    mt={"sm"}
                                    label="Social Security Number"
                                    description="Enter your full nine digits for verification."
                                    required={usingSsnBackup}
                                    inputMode="numeric"
                                    autoComplete="off"
                                    pattern="[0-9]{9}"
                                    minLength={9}
                                    maxLength={9}
                                    name="ssn"
                                    value={ssn}
                                    onChange={handleChange}
                                    onBlur={() => handleFieldBlur("ssn")}
                                    data-testid="ssn-input"
                                />
                            </Collapse>
                        </Box>
                    </Stack>

                    <Box my={4}>
                        {result.isError && (
                            <Text my={"md"} color="cs-red.5">
                                There was an error logging you in. Please check
                                the information and try again.
                            </Text>
                        )}
                        <Button
                            type="submit"
                            loading={result?.isLoading}
                            mt={"lg"}
                        >
                            LOGIN
                        </Button>
                    </Box>
                </form>
            </Card>
        </Container>
    );
}
